import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, Spinner } from './shared';

const EVENT_VARIANT = {
    created: 'success',
    hit: 'info',
    miss_recreate: 'warning',
    invalidated: 'danger',
    expired: 'outline',
};

function StatCard({ label, value, tone = 'default' }) {
    const colors = {
        default: 'text-gray-800',
        success: 'text-emerald-700',
        warning: 'text-amber-700',
        danger: 'text-red-700',
    };
    return (
        <div className="border border-gray-200 rounded-lg bg-white p-3">
            <div className="text-[11px] text-gray-500 uppercase">{label}</div>
            <div className={`text-sm font-semibold mt-1 ${colors[tone] || colors.default}`}>{value}</div>
        </div>
    );
}

export default function AdminGeminiCacheDetail() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const cacheKey = params.get('key');
    const [entry, setEntry] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => { if (cacheKey) load(); }, [cacheKey]);

    const load = async () => {
        try {
            setLoading(true);
            const [e, ev] = await Promise.all([
                adminApi.getGeminiCacheDetail(cacheKey),
                adminApi.listGeminiCacheEvents(cacheKey, { limit: 100 }),
            ]);
            setEntry(e);
            setEvents(ev.items || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    };

    const handleInvalidate = async () => {
        if (!confirm(`Force invalidate cache "${cacheKey}"?`)) return;
        try {
            await adminApi.invalidateGeminiCache(cacheKey);
            setSuccessMessage('Cache invalidated — next query will recreate.');
            setTimeout(() => setSuccessMessage(null), 4000);
            load();
        } catch (err) {
            setError(err.message || 'Invalidate failed');
        }
    };

    if (loading) return <Spinner size="lg" text="Loading cache..." center />;
    if (!entry) return <Alert variant="error">Cache entry not found</Alert>;

    const ragStale = entry.invalidated_at
        ? 'danger'
        : new Date(entry.expires_at).getTime() > Date.now()
            ? 'success'
            : 'warning';
    const ttlLabel = entry.invalidated_at
        ? `invalidated (${entry.invalidation_reason || 'manual'})`
        : new Date(entry.expires_at).getTime() > Date.now()
            ? 'active'
            : 'expired';

    return (
        <div>
            <div className="flex items-center justify-between gap-3 mb-6">
                <div className="min-w-0">
                    <Button variant="ghost" size="xs" onClick={() => navigate('/admin/gemini-caches')}>← Back</Button>
                    <h1 className="text-lg font-semibold text-gray-900 mt-1 font-mono break-all">{entry.cache_key}</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        <Badge variant="outline">{entry.ctx_type}</Badge>
                        <span className="mx-2">·</span>
                        <span className="font-mono">{entry.ref}</span>
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={load}>Refresh</Button>
                    {!entry.invalidated_at && (
                        <Button
                            size="sm"
                            className="text-red-600 hover:bg-red-50 border-red-200"
                            variant="outline"
                            onClick={handleInvalidate}
                        >
                            Force invalidate
                        </Button>
                    )}
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <div className="grid grid-cols-4 gap-3 mb-4">
                <StatCard label="Status" value={ttlLabel} tone={ragStale} />
                <StatCard label="Model" value={entry.model_name} />
                <StatCard label="Tokens cached" value={(entry.token_count || 0).toLocaleString()} />
                <StatCard label="Content size" value={`${(entry.content_size || 0).toLocaleString()} chars`} />
            </div>

            <div className="grid grid-cols-4 gap-3 mb-6">
                <StatCard label="Total hits" value={entry.total_hits} tone="success" />
                <StatCard label="Total cached tokens" value={(entry.total_cached_tokens || 0).toLocaleString()} />
                <StatCard label="Total output tokens" value={(entry.total_output_tokens || 0).toLocaleString()} />
                <StatCard label="Total cost (USD)" value={`$${Number(entry.total_cost_usd || 0).toFixed(4)}`} />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6 text-xs font-mono">
                <div className="border border-gray-200 rounded-lg bg-white p-3">
                    <div className="text-[11px] text-gray-500 uppercase mb-2">Lifecycle</div>
                    <div>remote_id: <span className="text-gray-700">{entry.remote_id || '—'}</span></div>
                    <div>display_name: <span className="text-gray-700">{entry.display_name || '—'}</span></div>
                    <div>ttl_seconds: <span className="text-gray-700">{entry.ttl_seconds}</span></div>
                    <div>created_at: <span className="text-gray-700">{new Date(entry.created_at).toLocaleString()}</span></div>
                    <div>expires_at: <span className="text-gray-700">{new Date(entry.expires_at).toLocaleString()}</span></div>
                    {entry.invalidated_at && (
                        <div>invalidated_at: <span className="text-red-600">{new Date(entry.invalidated_at).toLocaleString()}</span></div>
                    )}
                </div>
                <div className="border border-gray-200 rounded-lg bg-white p-3">
                    <div className="text-[11px] text-gray-500 uppercase mb-2">Attribution</div>
                    <div>triggered_by: <span className="text-gray-700">{entry.triggered_by_user_id || '—'}</span></div>
                    <div>last_used_by: <span className="text-gray-700">{entry.last_used_by_user_id || '—'}</span></div>
                    <div>last_used_at: <span className="text-gray-700">{entry.last_used_at ? new Date(entry.last_used_at).toLocaleString() : '—'}</span></div>
                </div>
            </div>

            <div className="mb-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-2">Content preview (4KB)</h2>
                <pre className="text-xs font-mono bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
{entry.content_preview || '(none)'}
                </pre>
            </div>

            <div className="mb-6">
                <h2 className="text-sm font-semibold text-gray-700 mb-2">System instruction</h2>
                <pre className="text-xs font-mono bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">
{entry.system_preview || '(none)'}
                </pre>
            </div>

            <div>
                <h2 className="text-sm font-semibold text-gray-700 mb-2">Event log ({events.length})</h2>
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-3 py-2 font-medium text-gray-500">When</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-500">Event</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-500">User</th>
                                <th className="text-right px-3 py-2 font-medium text-gray-500">Cached</th>
                                <th className="text-right px-3 py-2 font-medium text-gray-500">Fresh</th>
                                <th className="text-right px-3 py-2 font-medium text-gray-500">Output</th>
                                <th className="text-right px-3 py-2 font-medium text-gray-500">Cost</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {events.map((e) => (
                                <tr key={e.id}>
                                    <td className="px-3 py-2 font-mono text-gray-500">{new Date(e.created_at).toLocaleString()}</td>
                                    <td className="px-3 py-2">
                                        <Badge variant={EVENT_VARIANT[e.event_type] || 'default'}>{e.event_type}</Badge>
                                    </td>
                                    <td className="px-3 py-2 font-mono">{e.user_id || '—'}</td>
                                    <td className="px-3 py-2 text-right font-mono">{e.cached_input_tokens || '—'}</td>
                                    <td className="px-3 py-2 text-right font-mono">{e.fresh_input_tokens || '—'}</td>
                                    <td className="px-3 py-2 text-right font-mono">{e.output_tokens || '—'}</td>
                                    <td className="px-3 py-2 text-right font-mono">${Number(e.cost_usd || 0).toFixed(4)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
